import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { OpportunitiesFilters } from "../schemas";
import { parseSortDirection } from "@/components/tables/types";

const DEFAULT_START_DATE = dayjs().subtract(28, "day").toDate();
const DEFAULT_END_DATE = dayjs().toDate();
const DEFAULT_PAGE_SIZE = 10;

export function useExplorerFiltersFromUrl() {
  const [searchParams] = useSearchParams();

  // Pagination
  const page = useMemo(() => {
    const pageParam = searchParams.get("page");
    return pageParam ? parseInt(pageParam, 10) - 1 : 0;
  }, [searchParams]);

  const pageSize = useMemo(() => {
    const sizeParam = searchParams.get("size");
    return sizeParam ? parseInt(sizeParam, 10) : DEFAULT_PAGE_SIZE;
  }, [searchParams]);

  // Get date filters - check URL first, then use defaults
  const startDate = useMemo(() => {
    const urlValue = searchParams.get("start_date");
    if (urlValue) return new Date(urlValue);
    return DEFAULT_START_DATE;
  }, [searchParams]);

  const endDate = useMemo(() => {
    const urlValue = searchParams.get("end_date");
    if (urlValue) return new Date(urlValue);
    return DEFAULT_END_DATE;
  }, [searchParams]);

  const period = useMemo(() => searchParams.get("period") || "day", [searchParams]);

  // Sort parameters
  const gridSort = useMemo(() => searchParams.get("sort") || "cost_savings_max", [searchParams]);

  const gridSortDirection = useMemo(
    () => parseSortDirection(searchParams.get("sort_order"), "desc"),
    [searchParams]
  );

  // Filter values from URL
  const effortFilter = useMemo(() => searchParams.getAll("effort"), [searchParams]);

  const resourceNameFilter = useMemo(() => searchParams.getAll("resource_name"), [searchParams]);

  const opportunityNameFilter = useMemo(
    () => searchParams.getAll("opportunity_name"),
    [searchParams]
  );

  const resourceTypeFilter = useMemo(() => searchParams.getAll("resource_type"), [searchParams]);

  const ownerFilter = useMemo(() => searchParams.getAll("owner"), [searchParams]);

  const statusFilter = useMemo(() => searchParams.getAll("status"), [searchParams]);

  const userFilter = useMemo(() => searchParams.getAll("user"), [searchParams]);

  const warehouseFilter = useMemo(() => searchParams.getAll("warehouse"), [searchParams]);

  const instanceFilter = useMemo(() => searchParams.getAll("instance"), [searchParams]);

  // Build API filter params
  const filterParams = useMemo(() => {
    const params: OpportunitiesFilters = {
      page: page + 1,
      size: pageSize,
      start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
      end_date:
        getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() || new Date().toISOString(),
      period,
      sort: gridSort,
      sort_order: gridSortDirection,
    };

    if (effortFilter.length > 0) params.effort = effortFilter;
    if (resourceNameFilter.length > 0) params.resource_name = resourceNameFilter;
    if (opportunityNameFilter.length > 0) params.opportunity_name = opportunityNameFilter;
    if (resourceTypeFilter.length > 0) params.resource_type = resourceTypeFilter;
    if (ownerFilter.length > 0) params.owner = ownerFilter;
    if (statusFilter.length > 0) params.status = statusFilter;
    if (userFilter.length > 0) params.user = userFilter;
    if (warehouseFilter.length > 0) params.warehouse = warehouseFilter;
    if (instanceFilter.length > 0) params.instance_id = instanceFilter;

    return params;
  }, [
    page,
    pageSize,
    startDate,
    endDate,
    period,
    gridSort,
    gridSortDirection,
    effortFilter,
    resourceNameFilter,
    opportunityNameFilter,
    resourceTypeFilter,
    ownerFilter,
    statusFilter,
    userFilter,
    warehouseFilter,
    instanceFilter,
  ]);

  return {
    filterParams,
    // Individual filter values for components that need them
    startDate,
    endDate,
    period,
    gridSort,
    gridSortDirection,
    effortFilter,
    resourceNameFilter,
    opportunityNameFilter,
    resourceTypeFilter,
    ownerFilter,
    statusFilter,
    userFilter,
    warehouseFilter,
    instanceFilter,
    page,
    pageSize,
  };
}
